// v10/src/components/RegisterAeroclub.js
// v1.1 — Fix i18n: PLANS movido a función getPlans(t) para evitar t() a nivel de módulo
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Check, Loader, AlertCircle, MapPin, Building2 } from 'lucide-react';
import HUDBackground from './HUDBackground';
import { useTranslation } from 'react-i18next';

const getPlans = (t) => [
  { value: 'trial', label: t('plan_trial_label',{ns:'admin'}), desc: t('plan_trial_desc',{ns:'admin'}) },
  { value: 'basic', label: 'Basic — $30 USD/mes',    desc: 'Hasta 10 pilotos · features esenciales' },
  { value: 'pro',   label: 'Pro — $80 USD/mes',      desc: 'Pilotos ilimitados · todas las features' },
  { value: 'enterprise', label: 'Enterprise — $120 USD/mes', desc: '🔜 Próximamente — Q3 2026', disabled: true },
];

const RegisterAeroclub = ({ onBack }) => {
  const { t } = useTranslation();
  const PLANS = getPlans(t);

  const [step, setStep]       = useState(1); // 1: org, 2: admin, 3: done
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [detecting, setDetecting] = useState(false);

  const [form, setForm] = useState({
    org_name:       '',
    org_slug:       '',
    icao_code:      '',
    plan:           'trial',
    admin_name:     '',
    admin_email:    '',
    admin_password: '',
    admin_confirm:  '',
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Auto-generar slug desde nombre
  useEffect(() => {
    const slug = form.org_name
      .toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    set('org_slug', slug);
  }, [form.org_name]);

  // Detectar ICAO por GPS
  const detectLocation = () => {
    if (!navigator.geolocation) return;
    setDetecting(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const API = process.env.REACT_APP_API_URL || 'https://condor360-backend.onrender.com';
          const res = await fetch(
            `${API}/api/airports/nearest?lat=${pos.coords.latitude}&lng=${pos.coords.longitude}&radius=20`
          );
          const data = await res.json();
          if (data.success && data.found) {
            set('icao_code', data.data.icao_code || '');
            if (!form.org_name && data.data.name)
              set('org_name', data.data.name);
          }
        } catch (_) {}
        finally { setDetecting(false); }
      },
      () => setDetecting(false),
      { timeout: 8000 }
    );
  };

  const handleSubmit = async () => {
    setError('');
    if (form.admin_password !== form.admin_confirm) {
      setError(t('passwords_no_match',{ns:'admin'})); return;
    }
    if (form.admin_password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres'); return;
    }
    setLoading(true);
    try {
      const API = process.env.REACT_APP_API_URL || 'https://condor360-backend.onrender.com';
      const res = await fetch(`${API}/api/register-org`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          org_name:       form.org_name,
          org_slug:       form.org_slug,
          icao_code:      form.icao_code || undefined,
          plan:           form.plan,
          admin_name:     form.admin_name,
          admin_email:    form.admin_email,
          admin_password: form.admin_password,
        }),
      });
      const data = await res.json();
      if (data.success) setStep(3);
      else setError(data.message || 'Error al registrar');
    } catch { setError('Error de conexión. Intentá de nuevo.'); }
    finally { setLoading(false); }
  };

  // ✅ FIX: cambiado text-white → text-gray-900 para que el texto sea visible sobre fondo blanco
  const inp = 'w-full px-4 py-3 rounded-xl border border-white/10 bg-white/06 text-gray-900 placeholder-white/25 outline-none focus:border-purple-400/60 focus:bg-white/09 transition-all text-sm font-medium';

  const Label = ({ children }) => (
    <label style={{ display:'block', fontSize:12, fontWeight:700, color:'rgba(255,255,255,0.55)', marginBottom:7, textTransform:'uppercase', letterSpacing:'0.06em' }}>
      {children}
    </label>
  );

  return (
    <div style={{ minHeight:'100vh', position:'relative', display:'flex', alignItems:'center', justifyContent:'center', padding:'16px', overflow:'hidden' }}>
      <HUDBackground />

      <motion.div initial={{ opacity:0, y:30 }} animate={{ opacity:1, y:0 }}
        style={{ position:'relative', zIndex:10, width:'100%', maxWidth:'480px' }}>

        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:24 }}>
          <motion.button onClick={onBack} whileHover={{ scale:1.05 }} whileTap={{ scale:0.95 }}
            style={{ width:40, height:40, borderRadius:12, background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.1)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'rgba(255,255,255,0.6)' }}>
            <ArrowLeft style={{ width:18, height:18 }} />
          </motion.button>
          <div>
            <h1 style={{ margin:0, fontSize:22, fontWeight:900, color:'white', letterSpacing:'-0.5px' }}>Registrar Aeroclub</h1>
            <p style={{ margin:0, fontSize:12, color:'rgba(255,255,255,0.4)' }}>Condor360 — Sistema de gestión aeronáutica</p>
          </div>
        </div>

        {/* Card */}
        <div style={{ borderRadius:28, overflow:'hidden', background:'rgba(10,15,30,0.75)', backdropFilter:'blur(28px)', border:'1px solid rgba(99,102,241,0.2)', boxShadow:'0 32px 80px rgba(0,0,0,0.6)', padding:'32px' }}>

          {/* Steps indicator */}
          {step < 3 && (
            <div style={{ display:'flex', gap:8, marginBottom:28 }}>
              {[1, 2].map(s => (
                <div key={s} style={{ flex:1, display:'flex', alignItems:'center', gap:8 }}>
                  <div style={{ width:28, height:28, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontWeight:800, fontSize:13,
                    background: step >= s ? 'linear-gradient(135deg,#7c3aed,#4f46e5)' : 'rgba(255,255,255,0.08)',
                    color: step >= s ? 'white' : 'rgba(255,255,255,0.3)',
                    boxShadow: step >= s ? '0 4px 12px rgba(124,58,237,0.4)' : 'none' }}>
                    {step > s ? <Check style={{ width:14, height:14 }} /> : s}
                  </div>
                  <span style={{ fontSize:12, fontWeight:600, color: step >= s ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.3)' }}>
                    {s === 1 ? 'Datos del aeroclub' : 'Datos del administrador'}
                  </span>
                  {s < 2 && <div style={{ flex:1, height:1, background:'rgba(255,255,255,0.08)' }} />}
                </div>
              ))}
            </div>
          )}

          {/* Step 1 — Org */}
          {step === 1 && (
            <motion.div initial={{ opacity:0, x:20 }} animate={{ opacity:1, x:0 }} className="space-y-4">
              <div>
                <Label>Nombre del aeroclub *</Label>
                <input value={form.org_name} onChange={e => set('org_name', e.target.value)}
                  className={inp} placeholder="Aeroclub Puerto Madryn" />
              </div>

              <div>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:7 }}>
                  <Label>Código ICAO</Label>
                  <motion.button type="button" onClick={detectLocation} disabled={detecting}
                    whileHover={{ scale:1.03 }} whileTap={{ scale:0.97 }}
                    style={{ display:'flex', alignItems:'center', gap:5, padding:'4px 10px', borderRadius:20, border:'1px solid rgba(99,102,241,0.3)', background:'rgba(99,102,241,0.1)', color:'rgba(165,180,252,0.9)', fontSize:11, fontWeight:700, cursor:'pointer' }}>
                    {detecting
                      ? <Loader style={{ width:12, height:12, animation:'spin 1s linear infinite' }} />
                      : <MapPin style={{ width:12, height:12 }} />}
                    {detecting ? 'Detectando...' : 'Detectar por GPS'}
                  </motion.button>
                </div>
                <input value={form.icao_code} onChange={e => set('icao_code', e.target.value.toUpperCase())}
                  className={inp} placeholder="SAVY" maxLength={10} />
                <p style={{ margin:'5px 0 0 4px', fontSize:11, color:'rgba(255,255,255,0.3)' }}>
                  Opcional — se detecta automáticamente con GPS
                </p>
              </div>

              <div>
                <Label>Plan</Label>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {PLANS.map(p => (
                    <div key={p.value} onClick={() => !p.disabled && set('plan', p.value)}
                      style={{ padding:'12px 14px', borderRadius:14, cursor: p.disabled ? 'not-allowed' : 'pointer', transition:'all 0.2s',
                        opacity: p.disabled ? 0.45 : 1,
                        background: p.disabled ? 'rgba(255,255,255,0.02)' : form.plan === p.value ? 'rgba(124,58,237,0.15)' : 'rgba(255,255,255,0.04)',
                        border: `1.5px solid ${p.disabled ? 'rgba(255,255,255,0.05)' : form.plan === p.value ? 'rgba(124,58,237,0.5)' : 'rgba(255,255,255,0.08)'}` }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <div style={{ width:16, height:16, borderRadius:'50%', border:`2px solid ${form.plan === p.value && !p.disabled ? '#7c3aed' : 'rgba(255,255,255,0.2)'}`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                          {form.plan === p.value && !p.disabled && <div style={{ width:8, height:8, borderRadius:'50%', background:'#7c3aed' }} />}
                        </div>
                        <div style={{ flex:1 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                            <p style={{ margin:0, fontSize:13, fontWeight:700, color: p.disabled ? 'rgba(255,255,255,0.4)' : 'white' }}>{p.label}</p>
                            {p.disabled && <span style={{ fontSize:10, fontWeight:700, padding:'1px 7px', borderRadius:20, background:'rgba(124,58,237,0.2)', color:'#a78bfa' }}>Próximamente</span>}
                          </div>
                          <p style={{ margin:0, fontSize:11, color:'rgba(255,255,255,0.4)' }}>{p.desc}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <motion.button onClick={() => { if (form.org_name) setStep(2); else setError('El nombre del aeroclub es requerido'); }}
                whileHover={{ scale:1.02 }} whileTap={{ scale:0.97 }}
                style={{ width:'100%', padding:15, background:'linear-gradient(135deg,#7c3aed,#4f46e5)', color:'white', border:'none', borderRadius:16, fontWeight:800, fontSize:16, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8, boxShadow:'0 8px 24px rgba(124,58,237,0.35)', fontFamily:'inherit' }}>
                Continuar →
              </motion.button>
            </motion.div>
          )}

          {/* Step 2 — Admin */}
          {step === 2 && (
            <motion.div initial={{ opacity:0, x:20 }} animate={{ opacity:1, x:0 }} className="space-y-4">
              <div>
                <Label>Nombre del administrador *</Label>
                <input value={form.admin_name} onChange={e => set('admin_name', e.target.value)}
                  className={inp} placeholder="Juan Pérez" />
              </div>
              <div>
                <Label>Email *</Label>
                <input type="email" value={form.admin_email} onChange={e => set('admin_email', e.target.value)}
                  className={inp} placeholder="admin@miaeroclub.com" />
              </div>
              <div>
                <Label>Contraseña *</Label>
                <input type="password" value={form.admin_password} onChange={e => set('admin_password', e.target.value)}
                  className={inp} placeholder="Mínimo 8 caracteres" />
              </div>
              <div>
                <Label>Confirmar contraseña *</Label>
                <input type="password" value={form.admin_confirm} onChange={e => set('admin_confirm', e.target.value)}
                  className={inp} placeholder="Repetí la contraseña" />
              </div>

              {error && (
                <motion.div initial={{ opacity:0, y:-6 }} animate={{ opacity:1, y:0 }}
                  style={{ padding:'12px 14px', borderRadius:12, background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.25)', display:'flex', alignItems:'center', gap:10 }}>
                  <AlertCircle style={{ width:16, height:16, color:'rgba(248,113,113,0.9)', flexShrink:0 }} />
                  <p style={{ margin:0, fontSize:13, color:'rgba(248,113,113,0.9)' }}>{error}</p>
                </motion.div>
              )}

              <div style={{ display:'flex', gap:10 }}>
                <button onClick={() => setStep(1)}
                  style={{ padding:'14px 20px', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:16, color:'rgba(255,255,255,0.5)', fontWeight:600, fontSize:14, cursor:'pointer', fontFamily:'inherit' }}>
                  ← Volver
                </button>
                <motion.button onClick={handleSubmit} disabled={loading}
                  whileHover={!loading ? { scale:1.02 } : {}} whileTap={!loading ? { scale:0.97 } : {}}
                  style={{ flex:1, padding:15, background:'linear-gradient(135deg,#7c3aed,#4f46e5)', color:'white', border:'none', borderRadius:16, fontWeight:800, fontSize:16, cursor:loading?'not-allowed':'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8, boxShadow:'0 8px 24px rgba(124,58,237,0.35)', fontFamily:'inherit' }}>
                  {loading
                    ? <><Loader style={{ width:18, height:18, animation:'spin 1s linear infinite' }} /> Enviando...</>
                    : <><Building2 style={{ width:18, height:18 }} /> Enviar solicitud</>
                  }
                </motion.button>
              </div>
            </motion.div>
          )}

          {/* Step 3 — Done */}
          {step === 3 && (
            <motion.div initial={{ opacity:0, scale:0.95 }} animate={{ opacity:1, scale:1 }} style={{ textAlign:'center', padding:'8px 0' }}>
              <motion.div animate={{ scale:[1,1.1,1] }} transition={{ duration:0.5 }}
                style={{ fontSize:56, marginBottom:16 }}>🎉</motion.div>
              <h2 style={{ margin:'0 0 10px', fontSize:24, fontWeight:900, color:'white' }}>¡Solicitud enviada!</h2>
              <p style={{ margin:'0 0 20px', fontSize:14, color:'rgba(255,255,255,0.5)', lineHeight:1.6 }}>
                El equipo de <strong style={{ color:'rgba(255,255,255,0.8)' }}>Condor360</strong> revisará tu solicitud y te contactará en las próximas <strong style={{ color:'rgba(165,180,252,0.9)' }}>24 horas</strong> al email registrado.
              </p>
              <div style={{ padding:'14px 16px', borderRadius:16, background:'rgba(124,58,237,0.1)', border:'1px solid rgba(124,58,237,0.2)', marginBottom:20, textAlign:'left' }}>
                <p style={{ margin:0, fontSize:12, color:'rgba(255,255,255,0.5)', lineHeight:1.6 }}>
                  📋 <strong style={{ color:'rgba(255,255,255,0.7)' }}>Aeroclub:</strong> {form.org_name}<br/>
                  {form.icao_code && <><span style={{ opacity:0 }}>📋 </span>✈️ <strong style={{ color:'rgba(255,255,255,0.7)' }}>ICAO:</strong> {form.icao_code}<br/></>}
                  📧 <strong style={{ color:'rgba(255,255,255,0.7)' }}>Email:</strong> {form.admin_email}
                </p>
              </div>
              <motion.button onClick={onBack} whileHover={{ scale:1.02 }} whileTap={{ scale:0.97 }}
                style={{ width:'100%', padding:14, background:'linear-gradient(135deg,#7c3aed,#4f46e5)', color:'white', border:'none', borderRadius:16, fontWeight:700, fontSize:15, cursor:'pointer', fontFamily:'inherit' }}>
                Volver al login
              </motion.button>
            </motion.div>
          )}
        </div>
      </motion.div>
      <style>{`@keyframes spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } } .space-y-4 > * + * { margin-top: 16px; }`}</style>
    </div>
  );
};

export default RegisterAeroclub;
